import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {useState} from "react";

import {formatParameterValue} from "@reva/shared/number-format";
import type {ValuationData} from "@reva/shared/valuation-data";

import {useRevaTranslation, useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {useNavigateWithQuery} from "../../hooks/navigationHooks";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectRevaId} from "../../redux/persistenceSlice";
import {fetchPersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {selectSpec} from "../../redux/specSlice";
import {collectDifferences} from "../../util/persistenceChangeUtil";

interface Props {
    persistenceFromApi: ValuationData;
    /** Snapshot der Persistenz zum Zeitpunkt des Dialog-Öffnens */
    persistenceSnapshot: ValuationData;
    /** Callback zum Schließen des übergeordneten Finalisierungs-Dialogs */
    onClose: () => void;
}

function DialogFinalizeValuationChanged({persistenceFromApi, persistenceSnapshot, onClose}: Props) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();
    const revaId = useRevaSelector(selectRevaId);
    const spec = useRevaSelector(selectSpec);
    const segment = useRevaSelector(selectSegment);
    const selectLang = useSelectByCurrentLanguage();
    const navigate = useNavigateWithQuery();
    const [confirmLoading, setConfirmLoading] = useState(false);

    const onConfirm = async () => {
        setConfirmLoading(true);
        void dispatch(fetchPersistence({id: revaId} as any))
            .then(() => navigate("/object/overview"))
            .then(() => onClose())
            .finally(() => setConfirmLoading(false));
    };

    const onFinalizeDialogClose = () => {
        onClose();
    };

    const specParams = (spec as any)?.apiParams?.VALUE;
    const changes = Object.entries(
        collectDifferences(
            persistenceFromApi as unknown as Record<string, unknown>,
            persistenceSnapshot as unknown as Record<string, unknown>,
        ),
    )
        .filter(([key]) => key === "addressQuery" || specParams?.[segment]?.[key])
        .map(([key, [apiValue, storeValue]]) => {
            if (key === "addressQuery") return {label: selectLang("Adresse", "address"), apiValue, storeValue};
            const parameterDef = specParams[segment][key];
            const parameterName = selectLang(parameterDef.nameDe, parameterDef.nameEn);
            const apiValueLabel = formatParameterValue(language, parameterDef, apiValue);
            const storeValueLabel = formatParameterValue(language, parameterDef, storeValue);
            return {label: parameterName, apiValue: apiValueLabel, storeValue: storeValueLabel};
        });

    const footerContent = (
        <>
            <Button
                id="finalizeCancelButton"
                label={t("finalizeDialogChanged.cancel")}
                icon={PrimeIcons.TIMES}
                severity="danger"
                className="mr-2"
                text
                onClick={onFinalizeDialogClose}
                disabled={confirmLoading}
            />
            <Button
                id="finalizeSubmitButton"
                label={t("finalizeDialogChanged.submit")}
                icon={PrimeIcons.CHECK}
                onClick={() => void onConfirm()}
                loading={confirmLoading}
                disabled={confirmLoading}
            />
        </>
    );
    return (
        <Dialog
            id="finalizeDialogChanged"
            header={t("finalizeDialogChanged.title")}
            style={{minWidth: "50rem", minHeight: "10rem"}}
            visible={true}
            onHide={onFinalizeDialogClose}
            closable={false}
            footer={footerContent}
        >
            <div className="flex flex-col">
                <Message
                    severity="error"
                    text={t("finalizeDialogChanged.confirm")}
                    className="block white-space-pre-line leading-normal"
                />
                <DataTable value={changes} size={"small"} stripedRows className={"mt-4"}>
                    <Column field={"label"} header={t("finalizeDialogChanged.headerLabel")} />
                    <Column field={"storeValue"} header={t("finalizeDialogChanged.headerStoreValue")} />
                    <Column field={"apiValue"} header={t("finalizeDialogChanged.headerApiValue")} />
                </DataTable>
            </div>
        </Dialog>
    );
}

export default DialogFinalizeValuationChanged;
